'use client';

import { useEffect, type ReactNode } from 'react';

import { motion } from 'framer-motion';

import { Button } from './button';
import { UiLink } from '../lib/ui-link';
import {
	SIGNIN_PRIVACY_HREF,
	SIGNIN_PRIVACY_LABEL,
	SIGNIN_TERMS_HREF,
	SIGNIN_TERMS_LABEL,
} from '@naumu/shared/data/onboarding-copy';

export type ErrorShellActionVariant = 'primary' | 'secondary';

export type ErrorShellAction = {
	label: string;
	/**
	 * Destination for actions that are really a NAVIGATION. Prefer it over an
	 * imperative `window.location.href` in a click handler: the button is then
	 * wrapped in `UiLink`, so the host app's link bridge resolves the href -
	 * which is what makes a cross-app target (`/docs` from the platform app)
	 * land on the right origin in local dev, where the two apps are two ports.
	 * `@naumu/ui` must never read an env var itself, so this seam is how it
	 * stays origin-agnostic.
	 */
	href?: string;
	onClick?: () => void;
	variant: ErrorShellActionVariant;
	icon?: ReactNode;
};

interface ErrorShellProps {
	icon?: ReactNode;
	title: string;
	description: string;
	/** Slotted between the text and the action buttons. Animated with the shell. */
	extra?: ReactNode;
	actions?: ErrorShellAction[];
	digest?: string;
	onView?: () => void;
}

const ICON_DELAY = 0.1;
const TEXT_DELAY = 0.35;
const EXTRA_DELAY = 0.6;
const ACTIONS_BASE_DELAY = 0.85;
const ACTIONS_STEP_DELAY = 0.15;
const DIGEST_DELAY = 1.2;

const buttonSpring = {
	type: 'spring' as const,
	stiffness: 280,
	damping: 18,
};

const variantMap: Record<ErrorShellActionVariant, 'solid-strong' | 'solid-muted'> = {
	primary: 'solid-strong',
	secondary: 'solid-muted',
};

export const ErrorShell = ({
	icon,
	title,
	description,
	extra,
	actions,
	digest,
	onView,
}: ErrorShellProps) => {
	useEffect(() => {
		onView?.();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<div className="dark fixed inset-0 z-0 flex flex-col overflow-x-hidden overflow-y-auto bg-background text-neutral">
			<header className="flex items-center justify-between px-6 py-4 sm:px-8 sm:py-5">
				{/*
				  UiLink, not a raw <a>: `/home` belongs to the PLATFORM app, and this
				  shell also renders on marketing (its 404 and route-error views).
				  The host's link bridge resolves the origin; with no provider
				  mounted (a global-error boundary above the root layout) it degrades
				  to the plain anchor this used to be.
				*/}
				<UiLink href="/home" className="shrink-0">
					<span className="text-[13px] font-[300] uppercase tracking-[0.2em] text-neutral">
						naumu
					</span>
				</UiLink>
			</header>

			<main className="flex flex-1 items-center justify-center px-5 py-10">
				<div className="flex w-full max-w-[400px] flex-col items-center gap-6 text-center">
					{icon && (
						<motion.div
							className="text-neutral-muted"
							initial={{ scale: 0.6, opacity: 0 }}
							animate={{ scale: 1, opacity: 1 }}
							transition={{
								delay: ICON_DELAY,
								type: 'spring',
								stiffness: 260,
								damping: 18,
							}}
						>
							{icon}
						</motion.div>
					)}

					<motion.div
						className="flex flex-col items-center gap-1.5"
						initial={{ opacity: 0, y: 8 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ delay: TEXT_DELAY, duration: 0.4, ease: 'easeOut' }}
					>
						<h1 className="text-[20px] font-semibold text-neutral">{title}</h1>
						<p className="text-[13px] text-neutral-muted">{description}</p>
					</motion.div>

					{extra && (
						<motion.div
							initial={{ opacity: 0, y: 8 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ delay: EXTRA_DELAY, duration: 0.4, ease: 'easeOut' }}
						>
							{extra}
						</motion.div>
					)}

					{actions && actions.length > 0 && (
						<div className="flex items-center gap-2">
							{actions.map((action, index) => (
								<motion.div
									key={action.label}
									initial={{ scale: 0.85, opacity: 0 }}
									animate={{ scale: 1, opacity: 1 }}
									transition={{
										...buttonSpring,
										delay: ACTIONS_BASE_DELAY + index * ACTIONS_STEP_DELAY,
									}}
								>
									{action.href ? (
										<UiLink href={action.href}>
											<Button
												variant={variantMap[action.variant]}
												size="md"
												onClick={action.onClick}
												type="button"
											>
												{action.icon}
												{action.label}
											</Button>
										</UiLink>
									) : (
										<Button
											variant={variantMap[action.variant]}
											size="md"
											onClick={action.onClick}
											type="button"
										>
											{action.icon}
											{action.label}
										</Button>
									)}
								</motion.div>
							))}
						</div>
					)}

					{digest && (
						<motion.p
							className="mt-2 text-[11px] font-mono text-neutral/30"
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							transition={{ delay: DIGEST_DELAY, duration: 0.4 }}
						>
							ERROR {digest}
						</motion.p>
					)}
				</div>
			</main>

			<footer className="flex items-center justify-center gap-6 px-6 py-8">
				{/* /legal/* is MARKETING; same seam reason as the wordmark above. */}
				<UiLink
					href={SIGNIN_TERMS_HREF}
					className="text-[14px] font-semibold text-neutral/40 transition-colors hover:text-neutral/70"
				>
					{SIGNIN_TERMS_LABEL}
				</UiLink>
				<UiLink
					href={SIGNIN_PRIVACY_HREF}
					className="text-[14px] font-semibold text-neutral/40 transition-colors hover:text-neutral/70"
				>
					{SIGNIN_PRIVACY_LABEL}
				</UiLink>
			</footer>
		</div>
	);
};
